import React, {
  ReactElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";

import { Icon } from "semantic-ui-react";

import { History } from "history";

import ModalVideoTutorial, { MenuHasVideoType } from "react-lib/apps/MEH/ModalVideoTutorial";
import GroupedTabPenta, { TabMenuGroup } from "./GroupedTabPenta";

export type TabPentaProps = {
  menuGroups?: readonly TabMenuGroup[];
  ungroupedLabel?: React.ReactNode;
  ungroupedShortLabel?: React.ReactNode;
  ungroupedIcon?: React.ReactNode;
  resizableMenu?: boolean;
  resizeMenuLabel?: string;
  style?: React.CSSProperties;
  activeItemStyle?: React.CSSProperties;
  activeStyle?: React.CSSProperties;
  collapsible?: boolean;
  data?: TabItem[];
  disabled?: boolean;
  history?: History;
  itemStyle?: React.CSSProperties;
  level?: number;
  mobileScreen?: boolean;
  padding?: string;
  renderEmpty?: React.ReactElement | null;
  selectedTabIndex?: number;
  tabWidth?: string;
  topTab?: React.ReactElement;
  tutorials?: Record<string, MenuHasVideoType[]>;
  onChangeSelectedTabName?: (name: React.ReactElement | string, index: number) => void;
  onTabChange?: (index: number) => void;
};

export type TabItem = {
  key: string;
  icon?: ReactElement | string | null;
  keepMounted?: boolean;
  name: React.ReactElement | string;
  render: () => React.ReactElement;
};

export interface TabPentaRef {
  changeTab: (index: number) => void;
  changeTabKey: (toKey: string, callback?: () => void) => void;
}

const FlatTabPenta = React.forwardRef<TabPentaRef, TabPentaProps>((props, forwardedRef) => {
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const [selectedTabName, setSelectedTabName] = useState<React.ReactElement | string>("");
  const [showTab, setShowTab] = useState<boolean>(false);
  const [isMenuCollapsed, setIsMenuCollapsed] = useState<boolean>(false);
  const [maxMenuWidth, setMaxMenuWidth] = useState<string>("250px");
  const [hoveredTooltip, setHoveredTooltip] = useState<{
    index: number;
    top: number;
    left: number;
  } | null>(null);
  const [modTutorial, setModTutorial] = useState<MenuHasVideoType[] | null>(null);

  const activeItem = useRef<HTMLDivElement | null>(null);
  const measureRef = useRef<HTMLDivElement | null>(null);

  const callbackRef = useRef<(() => void) | null>(null);

  useImperativeHandle(forwardedRef, () => ({
    changeTab(index: number) {
      const tabKey = props.data?.[index]?.key || "";

      setSelectedTabIndex(index);
      setSelectedTabName(tabKey);
      props.onChangeSelectedTabName?.(tabKey, index);
    },
    changeTabKey(toKey: string, callback?: () => void) {
      const index = (props.data ?? []).findIndex((item: TabItem) => item.key === toKey);

      if (index === -1) {
        return;
      }

      console.log("setSelectedTabIndex:", index);

      if (callback) {
        if (selectedTabIndex === index) {
          callback();
        } else {
          callbackRef.current = callback; // Store callback in ref
        }
      }

      const tabKey = props.data?.[index]?.key || "";

      setSelectedTabIndex(index);
      setSelectedTabName(tabKey);
      props.onChangeSelectedTabName?.(tabKey, index);
    },
  }));

  // Trigger callback after selectedTabIndex changes
  useEffect(() => {
    console.log(selectedTabIndex);

    if (callbackRef.current) {
      callbackRef.current();
      callbackRef.current = null; // Clear the callback after execution
    }
  }, [selectedTabIndex]);

  useEffect(() => {
    if (typeof props.selectedTabIndex === "number" && Number.isInteger(props.selectedTabIndex)) {
      console.log(" 1", props.selectedTabIndex);

      const tabKey = props.data?.[props.selectedTabIndex]?.key || "";

      setSelectedTabIndex(props.selectedTabIndex);
      setSelectedTabName(tabKey);

      props.onChangeSelectedTabName?.(tabKey, props.selectedTabIndex);

      return;
    }

    if (props.history && props.history.location.pathname !== "/") {
      try {
        const keys = props.history.location.pathname.split("/");

        console.log(props.history.location.pathname);
        console.log(keys, keys.length, props.level);

        if (keys.length > (props.level ?? 0)) {
          const key = keys[props.level ?? 0];
          const index = (props.data ?? []).map((item: TabItem) => item.key).indexOf(key);

          console.log(" 2", index);

          if (index !== -1) {
            const tabKey = props.data?.[index]?.key || "";

            setSelectedTabIndex(index);
            setSelectedTabName(tabKey);

            props.onChangeSelectedTabName?.(tabKey, index);
          }
        }
      } catch (error) {
        console.log(error);
      }
    }
  }, []);

  useEffect(() => {
    // console.log('useEffect: rops.selectedTabIndex ', props.selectedTabIndex);
    // console.log('useEffect: rops.selectedTabIndex ', typeof props.selectedTabIndex);
    if (typeof props.selectedTabIndex === "number" && Number.isInteger(props.selectedTabIndex)) {
      // console.log(" 3 selectedTabIndex: ", selectedTabIndex)
      // console.log(" 3 props.selectedTabIndex: ", props.selectedTabIndex)
      setSelectedTabIndex(props.selectedTabIndex);
      setSelectedTabName(props.data?.[props.selectedTabIndex]?.key || "");
    }
  }, [props.selectedTabIndex]);

  useEffect(() => {
    if (selectedTabName && typeof selectedTabIndex === "number") {
      const key = props.data?.[selectedTabIndex]?.key || "";

      if (key !== selectedTabName) {
        const foundIndex = (props.data ?? []).findIndex((item) => item.key === selectedTabName);

        const index = foundIndex === -1 ? 0 : foundIndex;
        const tabKey = props.data?.[index]?.key || "";

        if (tabKey) {
          setSelectedTabIndex(index);
          setSelectedTabName(tabKey);
          props.onChangeSelectedTabName?.(selectedTabName, index);
        }
      }
    }
  }, [props.data]);

  const getTextContent = useCallback((name: React.ReactElement | string): string => {
    const extractText = (node: React.ReactNode): string => {
      if (typeof node === "string") {
        return node;
      }

      if (React.isValidElement(node)) {
        const children = React.Children.toArray((node.props as any).children);

        return children.map((child) => extractText(child)).join("");
      }

      return "";
    };

    return extractText(name).replaceAll(/\d+/g, "");
  }, []);

  const hasBadgeInName = useCallback((node: React.ReactNode): boolean => {
    if (!React.isValidElement(node)) {
      return false;
    }
    if ((node.props as any).circular) {
      return true;
    }

    return React.Children.toArray((node.props as any).children).some(hasBadgeInName);
  }, []);

  // Calculate maximum width based on menu names
  useEffect(() => {
    if (!props.data || props.data.length === 0 || !measureRef.current) {
      return;
    }

    const padding = 10; // Account for padding in menu items (5px * 2)
    const extraSpace = 20; // Additional space for comfort
    const tutorialIconSize = 42; // icon 33px + gap 8px
    const badgeWidth = 22;

    let maxWidth = 0;

    for (const item of props.data) {
      const tempSpan = document.createElement("span");

      tempSpan.style.visibility = "hidden";
      tempSpan.style.position = "absolute";
      tempSpan.style.whiteSpace = "nowrap";
      tempSpan.style.font = globalThis.getComputedStyle(measureRef.current!).font;

      // Get text content from name
      tempSpan.textContent = getTextContent(item.name);

      document.body.append(tempSpan);

      const hasTutorial = (props.tutorials?.[item.key] ?? []).length > 0;
      const hasBadge = hasBadgeInName(item.name);
      const itemWidth = tempSpan.offsetWidth + (hasTutorial ? tutorialIconSize : 0) + (hasBadge ? badgeWidth : 0);

      tempSpan.remove();

      maxWidth = Math.max(maxWidth, itemWidth);
    }

    const finalMaxWidth = Math.max(
      maxWidth + padding + extraSpace,
      Number.parseInt(props.tabWidth || "250")
    );

    setMaxMenuWidth(`${finalMaxWidth}px`);
  }, [getTextContent, props.data, props.tabWidth, props.tutorials]);

  const getFirstChar = useCallback((text: string) => {
    // หาพยัชนะไทยตัวแรกในข้อความ (ก-ฮ)
    // ถ้าไม่เจอ ให้ใช้ตัวอักษรแรกแล้วแปลงเป็นตัวพิมพ์ใหญ่
    const match = /[\u0E01-\u0E2E]/.exec(text);

    return match ? match[0] : text.charAt(0).toUpperCase();
  }, []);

  const getDisplayContent = useCallback(
    (item: TabItem, isActive: boolean) => {
      if (!isMenuCollapsed) {
        const tutorial = props.tutorials?.[item.key] ?? [];

        return (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div style={{ width: "100%" }}>{item.name}</div>
            {tutorial.length > 0 && isActive && (
              <img
                style={{ marginLeft: "10px", width: "32px" }}
                src="/static/images/menus/video-tutorial.png"
                onClick={(e) => {
                  e.stopPropagation();
                  setModTutorial(tutorial);
                }}
              />
            )}
          </div>
        );
      }

      if (item.icon) {
        return typeof item.icon === "string" ? (
          <img
            style={{ height: "20px", objectFit: "contain", width: "20px" }}
            alt="menu.icon"
            src={item.icon}
          />
        ) : (
          item.icon
        );
      }

      return <>{getFirstChar(getTextContent(item.name))}</>;
    },
    [getFirstChar, getTextContent, isMenuCollapsed, props.tutorials]
  );

  const menuWidth: string = props.tabWidth || "250px";
  const collapsedWidth = "50px";
  const currentMenuWidth: string = isMenuCollapsed ? collapsedWidth : menuWidth;
  const currentMaxMenuWidth: string = isMenuCollapsed ? collapsedWidth : maxMenuWidth;

  return (
    <div
      id="TabPenta"
      style={{
        display: "flex",
        flexDirection: props.mobileScreen ? "column" : "row",
        ...(props.mobileScreen ? { overflow: "scroll" } : { height: "100%" }),
        width: "100%",
      }}
    >
      <div
        ref={measureRef}
        id="TabPenta-Menu"
        style={{
          border: "solid #cccccc 1px",
          display: "flex",
          flexDirection: "column",
          maxHeight: "95vh",
          overflow: "hidden",
          transition: "width 0.075s ease, max-width 0.075s ease, min-width 0.075s ease",
          ...(props.mobileScreen
            ? { width: "100%" }
            : { maxWidth: currentMaxMenuWidth, minWidth: currentMenuWidth }),
          ...props.style,
        }}
      >
        {props.collapsible && (
          <div
            style={{
              borderBottom: "1px solid #cccccc",
              color: "var(--primary-theme-color)",
              cursor: "pointer",
              display: "flex",
              justifyContent: isMenuCollapsed ? "center" : "",
              padding: "3px 12px",
            }}
            onClick={() => {
              setIsMenuCollapsed(!isMenuCollapsed);
            }}
          >
            <div
              style={{
                borderBottomWidth: "1px",
                borderColor: "var(--primary-theme-color)",
                borderRadius: "4px",
                borderStyle: "solid",
                borderTopWidth: "1px",
                width: "23px",
                ...(isMenuCollapsed
                  ? { borderLeftWidth: "1px", borderRightWidth: "3px", paddingLeft: "2px" }
                  : { borderLeftWidth: "3px", borderRightWidth: "1px" }),
              }}
            >
              <Icon name={isMenuCollapsed ? "angle right" : "angle left"} />
            </div>
          </div>
        )}

        <div
          style={{
            flex: 1,
            overflowX: "hidden",
            overflowY: "auto",
          }}
        >
          {[
            ...(props.topTab ? [props.topTab] : []),
            ...(props.data ?? []).map(
              (item: TabItem, index: number) =>
                // console.log("TabPenta item", item)
                // console.log("TabPenta index", index)
                (!props.mobileScreen || showTab || selectedTabIndex === index) && (
                  <div
                    key={item.key}
                    ref={props.data?.[selectedTabIndex]?.key === item.key ? activeItem : null}
                    id={`tab-${item.key}`}
                    className={props.data?.[selectedTabIndex]?.key === item.key ? "active" : ""}
                    style={{
                      ...(props.data?.[selectedTabIndex]?.key === item.key
                        ? {
                            backgroundColor: "var(--primary-select-background)",
                            ...props.activeStyle,
                          }
                        : { backgroundColor: "white" }),
                      alignItems: "center",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: isMenuCollapsed ? "center" : "flex-start",
                      minHeight: "30px",
                      padding: props.padding || "5px",
                      ...props.itemStyle,
                      ...(item.icon && { maxHeight: "30px" }),
                    }}
                    onMouseEnter={(e) => {
                      if (isMenuCollapsed) {
                        const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();

                        setHoveredTooltip({ index, left: rect.right + 4, top: rect.top + rect.height / 2 });
                      }
                    }}
                    onMouseLeave={() => setHoveredTooltip(null)}
                    onClick={() => {
                      if (props.disabled) {
                        return;
                      }

                      if (props.mobileScreen) {
                        setShowTab(!showTab);
                      }

                      const tabKey = props.data?.[index]?.key || "";

                      setSelectedTabIndex(index);
                      setSelectedTabName(tabKey);
                      props.onChangeSelectedTabName?.(tabKey, index);

                      if (props.history && props.data) {
                        try {
                          const keys = props.history.location.pathname.split("/");

                          if (keys.length > (props.level ?? 0)) {
                            keys[props.level ?? 0] = props.data[index].key;
                            keys.splice((props.level ?? 0) + 1);
                            props.history.push(keys.join("/") + globalThis.location.search);
                          } else if (keys.length === (props.level ?? 0)) {
                            keys.push(props.data[index].key);
                            props.history.push(keys.join("/") + globalThis.location.search);
                          }
                        } catch (error) {
                          console.log(error);
                        }
                      }

                      props.onTabChange?.(index);
                    }}
                  >
                    <span
                      style={{
                        ...(props.data?.[selectedTabIndex]?.key === item.key
                          ? { color: "black", ...props.activeItemStyle }
                          : { color: "var(--primary-theme-color-dark)" }),
                        width: "100%",
                        ...(isMenuCollapsed && {
                          alignItems: "center",
                          display: "flex",
                          justifyContent: "center",
                        }),
                      }}
                    >
                      {getDisplayContent(item, props.data?.[selectedTabIndex]?.key === item.key)}
                    </span>
                  </div>
                )
            ),
          ]}
        </div>
      </div>
      <div
        style={{
          flex: 1,
          width: `calc(100% - ${currentMaxMenuWidth})`,
        }}
      >
        {(props.data ?? []).map((item, index) =>
          item.keepMounted ? (
            <div
              key={item.key}
              style={
                index === selectedTabIndex
                  ? { height: "100%" }
                  : { display: "none" }
              }
            >
              {item.render()}
            </div>
          ) : null
        )}
        {!props.data?.[selectedTabIndex]?.keepMounted &&
          (props.data?.[selectedTabIndex]?.render() ?? "")}
        {!!props.renderEmpty && (props.data ?? []).length === 0 && props.renderEmpty}
      </div>

      {modTutorial && (
        <ModalVideoTutorial
          videos={modTutorial}
          open
          onClose={() => {
            setModTutorial(null);
          }}
        />
      )}

      {hoveredTooltip &&
        ReactDOM.createPortal(
          <div
            style={{
              backgroundColor: "white",
              border: "1px solid #cccccc",
              borderRadius: "4px",
              boxShadow: "2px 2px 6px rgba(0,0,0,0.15)",
              color: "black",
              fontSize: "13px",
              left: hoveredTooltip.left,
              padding: "5px 10px",
              pointerEvents: "none",
              position: "fixed",
              top: hoveredTooltip.top,
              transform: "translateY(-50%)",
              whiteSpace: "nowrap",
              zIndex: 9999,
            }}
          >
            {props.data?.[hoveredTooltip.index]?.name}
          </div>,
          document.body
        )}
    </div>
  );
});

const TabPenta = React.forwardRef<TabPentaRef, TabPentaProps>((props, ref) => {
  const grouped = !!props.menuGroups?.length && !props.mobileScreen;
  const previousMode = useRef(grouped);
  const previousIndexProp = useRef(props.selectedTabIndex);
  const selectedKey = useRef<string>("");
  const transitionIndex = useRef<number | undefined>(undefined);

  if (previousMode.current !== grouped) {
    const index = (props.data || []).findIndex((item) => item.key === selectedKey.current);
    transitionIndex.current = index >= 0 ? index : props.selectedTabIndex;
    previousMode.current = grouped;
  }
  if (previousIndexProp.current !== props.selectedTabIndex) {
    transitionIndex.current = undefined;
    previousIndexProp.current = props.selectedTabIndex;
  }

  const onChangeSelectedTabName = (name: React.ReactElement | string, index: number) => {
    selectedKey.current = props.data?.[index]?.key || "";
    props.onChangeSelectedTabName?.(name, index);
  };
  const sharedProps = {
    ...props,
    selectedTabIndex: transitionIndex.current ?? props.selectedTabIndex,
    onChangeSelectedTabName,
  };
  return grouped ? <GroupedTabPenta {...sharedProps} ref={ref} /> : <FlatTabPenta {...sharedProps} ref={ref} />;
});

TabPenta.displayName = "TabPenta";

export default TabPenta;
